import React, { useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import LeadForm from '../components/LeadForm';
import { SEO_PAGES } from '../seo/seoPages';

export default function SEOLandingPage() {
    const location = useLocation();
    const slug = location.pathname.replace(/^\//, '');
    const navigate = useNavigate();
    const page = SEO_PAGES[slug];

    useEffect(() => {
        if (page) {
            document.title = page.title;
            const meta = document.querySelector('meta[name="description"]');
            if (meta) meta.setAttribute('content', page.meta);
        }
        return () => {
            document.title = 'Cruise Vibes Travel Co. — Luxury Party Cruises';
        };
    }, [page]);

    if (!page) {
        return (
            <div className="max-w-2xl mx-auto py-24 text-center text-white/60">
                <h1 className="font-display text-3xl uppercase tracking-tight mb-4">Page not found</h1>
                <Link to="/" className="text-brand-aqua hover:underline">Back home →</Link>
            </div>
        );
    }

    return (
        <div data-testid={`seo-page-${slug}`}>
            <section className="relative min-h-[60vh] flex items-end overflow-hidden">
                <img src={page.image} alt="" className="absolute inset-0 h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-b from-transparent via-brand-bg/25 to-brand-bg/90" />
                <div className="relative z-10 max-w-7xl mx-auto px-5 lg:px-10 pb-14 pt-32 w-full">
                    <nav className="text-xs uppercase tracking-[0.3em] text-brand-aqua mb-5 flex items-center gap-2 flex-wrap">
                        <Link to="/" className="hover:text-white">Home</Link>
                        <ChevronRight size={12} />
                        <span className="text-white/70">{page.accent}</span>
                    </nav>
                    <h1 className="font-display text-5xl sm:text-7xl font-black uppercase tracking-tighter max-w-5xl">
                        {page.h1}
                    </h1>
                    <p className="text-white/85 text-lg max-w-2xl mt-6 leading-relaxed">{page.intro}</p>
                    <div className="flex flex-wrap gap-3 mt-8">
                        <Button
                            onClick={() => {
                                if (window.location.pathname !== '/') navigate('/');
                                setTimeout(() => document.querySelector('[data-testid="home-lead-form"]')?.scrollIntoView({ behavior: 'smooth' }), 200);
                            }}
                            className="btn-sunset rounded-full h-12 px-7 uppercase tracking-widest font-semibold"
                        >
                            Request a Quote
                        </Button>
                    </div>
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
                <div className="space-y-12">
                    {page.sections.map((s, i) => (
                        <article key={i} className="grid lg:grid-cols-3 gap-6">
                            <h2 className="font-display text-2xl sm:text-3xl font-bold uppercase tracking-tight">
                                <span className="font-serif-italic font-medium text-brand-gold">{String(i + 1).padStart(2, '0')}.</span> {s.h}
                            </h2>
                            <p className="lg:col-span-2 text-white/75 leading-relaxed">{s.p}</p>
                        </article>
                    ))}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16">
                <div className="mb-10">
                    <div className="text-xs uppercase tracking-[0.25em] text-brand-aqua mb-3">Frequently asked</div>
                    <h2 className="font-display text-4xl font-bold uppercase tracking-tight">Good <span className="font-serif-italic font-medium text-brand-gold">questions</span>.</h2>
                </div>
                <div className="space-y-3">
                    {page.faqs.map((f, i) => (
                        <details key={i} className="bg-brand-surface border border-white/5 rounded-2xl p-5 hover:border-brand-aqua/30 transition-colors group">
                            <summary className="cursor-pointer flex justify-between items-center list-none">
                                <span className="font-display text-base sm:text-lg uppercase tracking-tight pr-4">{f.q}</span>
                                <i className="fa-solid fa-plus text-brand-aqua transition-transform group-open:rotate-45"></i>
                            </summary>
                            <p className="mt-4 text-white/70 text-sm leading-relaxed">{f.a}</p>
                        </details>
                    ))}
                </div>
            </section>

            <section className="max-w-7xl mx-auto px-5 lg:px-10 py-16 pb-24">
                <LeadForm />
            </section>
        </div>
    );
}
